"use client";
import React, { createContext, useContext, useEffect, useState } from "react";
import { useAuth } from "./AuthContext";
import { useOutlet } from "./OutletContext";
import {
  subscribeToCart,
  addToCart as addToCartFB,
  updateCartQuantity,
  removeFromCart as removeFromCartFB,
  clearCart as clearCartFB,
  getProductsByIds,
  subscribeToProductsByIds,
} from "@/firebase/firestoreServices";
import type { CartItem, Product } from "@/types";

interface Variant {
  id: string;
  price: number;
  name?: string;
  weight?: string;
}

interface CartContextType {
  items: CartItem[];
  totalItems: number;
  totalPrice: number;
  addToCart: (product: Product, variant: Variant) => Promise<void>;
  updateQuantity: (cartItemId: string, quantity: number) => Promise<void>;
  removeItem: (cartItemId: string) => Promise<void>;
  clearCart: () => Promise<void>;
  getItemQuantity: (productId: string, variantId: string) => number;
  getCartItemId: (productId: string, variantId: string) => string | null;
}

const CartContext = createContext<CartContextType>({
  items: [],
  totalItems: 0,
  totalPrice: 0,
  addToCart: async () => { },
  updateQuantity: async () => { },
  removeItem: async () => { },
  clearCart: async () => { },
  getItemQuantity: () => 0,
  getCartItemId: () => null,
});

export const useCart = () => useContext(CartContext);

export const CartProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, profile } = useAuth();
  const { selectedOutlet } = useOutlet();
  const [items, setItems] = useState<CartItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user || !selectedOutlet?.id) {
      setItems([]);
      setLoading(false);
      return;
    }

    const cartMap = profile?.userCartItems || {};
    const cartValues = Object.values(cartMap);

    if (cartValues.length === 0) {
      setItems([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    const productIds = Array.from(new Set(cartValues.map(item => item.foodDocId)));

    // Subscribe to product details (for real-time stock updates)
    const unsubscribe = subscribeToProductsByIds(productIds, selectedOutlet.id, (products) => {
      const mappedItems: CartItem[] = cartValues.map(item => {
        const product = products.find(p => p.id === item.foodDocId);
        return {
          id: item.itemId,
          productId: item.foodDocId,
          productName: item.productName || product?.name || "Product",
          productImage: item.image || (product?.images?.[0] || product?.image),
          price: item.price || product?.price || 0,
          quantity: item.qty,
          variantId: item.variantId || "",
          variantName: item.variantName || "",
          userId: user.uid,
          outOfStock: product?.outOfStock || product?.blocked || false,
        };
      });

      setItems(mappedItems);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [user, profile?.userCartItems, selectedOutlet?.id]);

  const totalItems = items.reduce((acc, item) => acc + item.quantity, 0);
  const totalPrice = items.reduce((acc, item) => acc + item.price * item.quantity, 0);

  const addToCart = async (product: Product, variant: Variant) => {
    if (!user) return;
    // Current web code usually adds to a separate collection, 
    // but for profile sync we should ideally update the profile map.
    // For now, I'll keep calling the FB service which I will update next.
    await addToCartFB(user.uid, product, variant);
  };

  const updateQuantity = async (cartItemId: string, quantity: number) => {
    if (!user) return;
    await updateCartQuantity(user.uid, cartItemId, quantity);
  };

  const removeItem = async (cartItemId: string) => {
    if (!user) return;
    await removeFromCartFB(user.uid, cartItemId);
  };

  const clearCart = async () => {
    if (!user) return;
    await clearCartFB(user.uid);
  };

  const getItemQuantity = (productId: string, _variantId: string) => {
    const item = items.find((i) => i.productId === productId);
    return item?.quantity || 0;
  };

  const getCartItemId = (productId: string, _variantId: string) => {
    const item = items.find((i) => i.productId === productId);
    return item?.id || null;
  };

  return (
    <CartContext.Provider
      value={{ items, totalItems, totalPrice, addToCart, updateQuantity, removeItem, clearCart, getItemQuantity, getCartItemId }}
    >
      {children}
    </CartContext.Provider>
  );
};
